import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserApi } from "../api/user-api";
import { useUser } from "@clerk/react";
import { SetUserFlagsDto, SubmitOnboardingSurveyDto } from "@repo/api/user";

export function useSelfQuery() {
  const { user, isLoaded, isSignedIn } = useUser();

  return useQuery({
    queryKey: userQueryKeys.getSelf(user?.id),
    queryFn: () => UserApi.getSelf(),
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch user details",
    },
    enabled: isLoaded && isSignedIn && !!user?.id,
  });
}

export function useIntercomMessengerPayloadQuery() {
  const { data: self } = useSelfQuery();

  return useQuery({
    queryKey: userQueryKeys.getIntercomMessengerPayload(self?.externalId),
    queryFn: () => UserApi.getIntercomMessengerPayload(),
    staleTime: Infinity,
    enabled: !!self?.externalId,
  });
}

export function useSubmitOnboardingSurveyMutation() {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: (data: SubmitOnboardingSurveyDto) =>
      UserApi.submitOnboardingSurvey(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.getSelf(user?.id),
      });
    },
  });
}

export function useSetUserFlagsMutation() {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: (data: SetUserFlagsDto) => UserApi.setUserFlags(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.getSelf(user?.id),
      });
    },
  });
}

export const userQueryKeys = {
  all: () => ["user"],
  getSelf: (userId?: string) => ["user", "self", { userId }],
  getIntercomMessengerPayload: (userId?: string) => [
    "user",
    "self",
    "intercomMessengerPayload",
    { userId },
  ],
};
